import { Component, ReactNode, ErrorInfo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

interface Props { children: ReactNode; }
interface State { hasError: boolean; message: string; }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, message: '' });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2, textAlign: 'center', p: 4 }}>
        <Typography variant="h5" fontWeight={600}>Something went wrong</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          An unexpected error occurred. Your data is safe — reload the page to continue.
        </Typography>
        {this.state.message && (
          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', maxWidth: 520, wordBreak: 'break-all' }}>
            {this.state.message}
          </Typography>
        )}
        <Button variant="contained" onClick={this.handleReload} sx={{ mt: 1 }}>
          Reload App
        </Button>
      </Box>
    );
  }
}
